import { Tariff } from "./types";
import dayjs from "dayjs";

// Check for incomplete tariffs (missing dates or prices)
export const getIncompleteTariffs = (tariffs: Tariff[]): Tariff[] => {
  return tariffs.filter(
    (tariff) => tariff.from === null || tariff.to === null || tariff.price === null
  );
};

// Check for tariffs that are expiring soon (within 3 days)
export const getSoonExpiringTariffs = (tariffs: Tariff[], daysThreshold = 3): Tariff[] => {
  const today = dayjs();
  const thresholdDate = today.add(daysThreshold, "day");

  return tariffs.filter((tariff) => {
    const endDate = tariff.to ? dayjs(tariff.to) : null;
    return endDate && endDate.isAfter(today) && endDate.isBefore(thresholdDate);
  });
};

// Get tariff status code
export type TariffStatus = "active" | "expiring" | "future" | "expired" | "incomplete";

// Get status of a tariff based on its dates and completeness
export const getTariffStatus = (tariff: Tariff): TariffStatus => {
  const today = dayjs();
  const startDate = tariff.from ? dayjs(tariff.from) : null;
  const endDate = tariff.to ? dayjs(tariff.to) : null;

  if (!startDate || !endDate || !tariff.price) {
    return "incomplete";
  }

  if (endDate.isBefore(today) && !endDate.isSame(today)) {
    return "expired";
  }

  if (startDate.isAfter(today)) {
    return "future";
  }

  if (endDate.diff(today, "day") <= 3) {
    return "expiring";
  }

  return "active";
};

// Get color for tariff status
export const getTariffStatusColor = (
  status: TariffStatus
): "success" | "warning" | "processing" | "default" | "error" => {
  switch (status) {
    case "active":
      return "success";
    case "expiring":
      return "warning";
    case "future":
      return "processing";
    case "expired":
      return "default";
    case "incomplete":
      return "error";
    default:
      return "default";
  }
};

// Get status color directly from tariff object
export const getTariffColor = (tariff: Tariff): "success" | "warning" | "processing" | "default" | "error" => {
  return getTariffStatusColor(getTariffStatus(tariff));
};

// Get human-readable status label
export const getTariffStatusLabel = (status: TariffStatus): string => {
  switch (status) {
    case "active":
      return "Active";
    case "expiring":
      return "Expiring Soon";
    case "future":
      return "Future";
    case "expired":
      return "Expired";
    case "incomplete":
      return "Incomplete";
    default:
      return "Unknown";
  }
};

// Format tariff date range as a string
export const formatTariffDateRange = (tariff: Tariff): string => {
  const from = tariff.from ? dayjs(tariff.from).format("YYYY-MM-DD") : null;
  const to = tariff.to ? dayjs(tariff.to).format("YYYY-MM-DD") : null;

  if (from && to) {
    return `${from} to ${to}`;
  } else if (from) {
    return `From ${from}`;
  } else if (to) {
    return `Until ${to}`;
  } else {
    return "No date range";
  }
};

// Get color based on transport type
export const getTransportTypeColor = (
  transportType: string
): "success" | "warning" | "processing" | "error" | "default" => {
  const lowerType = transportType.toLowerCase();
  
  if (lowerType.includes("air") || lowerType.includes("flight")) {
    return "processing";
  } else if (lowerType.includes("ocean") || lowerType.includes("sea")) {
    return "success";
  } else if (lowerType.includes("road") || lowerType.includes("truck")) {
    return "warning";
  } else if (lowerType.includes("rail") || lowerType.includes("train")) {
    return "error";
  }
  
  return "default";
};

// Get emoji for transport type
export const getTransportTypeEmoji = (transportType: string): string => {
  const lowerType = transportType.toLowerCase();
  
  if (lowerType.includes("air") || lowerType.includes("flight")) {
    return "✈️";
  } else if (lowerType.includes("ocean") || lowerType.includes("sea")) {
    return "🚢";
  } else if (lowerType.includes("road") || lowerType.includes("truck")) {
    return "🚚";
  } else if (lowerType.includes("rail") || lowerType.includes("train")) {
    return "🚂";
  }
  
  return "📦";
};

// Convert a tariff to a string summary with essential information
export const getTariffSummary = (tariff: Tariff): string => {
  const route = `${tariff.origin_port_data?.name || "Any"} → ${
    tariff.destination_port_data?.name || "Any"
  }`;
  const transport = tariff.transport_type_data?.name || "Any Transport";
  const price = tariff.price ? `€${tariff.price.toFixed(2)}` : "No price";
  
  return `${transport} | ${route} | ${price}`;
};

// Interface for a route combination
export interface RouteCombination {
  originPortId: number;
  destinationPortId: number;
  transportTypeId: number;
  bookingTypeId?: number;
  containerSizeId?: number;
  bracketId?: number;
  originPortName: string;
  destinationPortName: string;
  transportTypeName: string;
  bookingTypeName?: string;
  containerSizeName?: string;
  bracketName?: string;
  score: number;
  reason: string;
}

// Get missing route combinations based on existing tariffs
export const getMissingRouteCombinations = (
  tariffs: Tariff[],
  activeOnly: boolean = true
): RouteCombination[] => {
  // If activeOnly is true, only consider active, future, and expiring tariffs
  const relevantTariffs = activeOnly
    ? tariffs.filter(
        (tariff) => {
          const status = getTariffStatus(tariff);
          return status === "active" || status === "future" || status === "expiring";
        }
      )
    : tariffs;

  // Extract all unique origin-destination-transport combinations
  const existingCombinations = new Set<string>();
  const routeFrequency = new Map<string, number>();
  
  // Find all existing route combinations and track their frequency
  relevantTariffs.forEach(tariff => {
    if (tariff.origin_port && tariff.destination_port && tariff.transport_type) {
      const key = `${tariff.origin_port}-${tariff.destination_port}-${tariff.transport_type}`;
      
      existingCombinations.add(key);
      
      // Count by booking type
      if (tariff.bookings_type) {
        const bookingKey = `${key}-booking-${tariff.bookings_type}`;
        routeFrequency.set(bookingKey, (routeFrequency.get(bookingKey) || 0) + 1);
      }
      
      // Count by container size
      if (tariff.container_size) {
        const containerKey = `${key}-container-${tariff.container_size}`;
        routeFrequency.set(containerKey, (routeFrequency.get(containerKey) || 0) + 1);
      }
      
      // Count by bracket
      if (tariff.tariff_bracket) {
        const bracketKey = `${key}-bracket-${tariff.tariff_bracket}`;
        routeFrequency.set(bracketKey, (routeFrequency.get(bracketKey) || 0) + 1);
      }
    }
  });
  
  // Find popular routes (those with the most tariffs)
  const routePopularity = new Map<string, number>();
  existingCombinations.forEach(key => {
    let count = 0;
    relevantTariffs.forEach(tariff => {
      if (tariff.origin_port && tariff.destination_port && tariff.transport_type) {
        const tariffKey = `${tariff.origin_port}-${tariff.destination_port}-${tariff.transport_type}`;
        if (tariffKey === key) {
          count++;
        }
      }
    });
    routePopularity.set(key, count);
  });
  
  // Sort routes by popularity
  const sortedRoutes = Array.from(routePopularity.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10); // Consider only top 10 most popular routes
  
  const suggestions: RouteCombination[] = [];
  
  // For each popular route, check for missing components
  sortedRoutes.forEach(([routeKey, popularity]) => {
    const [originId, destId, transportId] = routeKey.split('-').map(Number);
    
    // Find all booking types used with this route
    const bookingTypes = new Set<number>();
    // Find all container sizes used with this route
    const containerSizes = new Set<number>();
    // Find all brackets used with this route
    const brackets = new Set<number>();
    
    relevantTariffs.forEach(tariff => {
      if (tariff.origin_port === originId && 
          tariff.destination_port === destId && 
          tariff.transport_type === transportId) {
        
        if (tariff.bookings_type) bookingTypes.add(tariff.bookings_type);
        if (tariff.container_size) containerSizes.add(tariff.container_size);
        if (tariff.tariff_bracket) brackets.add(tariff.tariff_bracket);
      }
    });
    
    // Find most frequent booking types across all routes
    const allBookingTypes = new Set<number>();
    relevantTariffs.forEach(t => {
      if (t.bookings_type) allBookingTypes.add(t.bookings_type);
    });
    
    // Find missing booking types for this route
    allBookingTypes.forEach(bookingTypeId => {
      if (!bookingTypes.has(bookingTypeId)) {
        // Look up the name of the booking type
        const bookingType = relevantTariffs.find(t => t.bookings_type === bookingTypeId)?.bookings_type_data;
        const originPort = relevantTariffs.find(t => t.origin_port === originId)?.origin_port_data;
        const destPort = relevantTariffs.find(t => t.destination_port === destId)?.destination_port_data;
        const transportType = relevantTariffs.find(t => t.transport_type === transportId)?.transport_type_data;
        
        if (bookingType && originPort && destPort && transportType) {
          suggestions.push({
            originPortId: originId,
            destinationPortId: destId,
            transportTypeId: transportId,
            bookingTypeId: bookingTypeId,
            originPortName: originPort.name,
            destinationPortName: destPort.name,
            transportTypeName: transportType.name,
            bookingTypeName: bookingType.name,
            score: popularity * 2, // Higher score for popular routes
            reason: `Popular route ${originPort.name} → ${destPort.name} is missing the ${bookingType.name} booking type`
          });
        }
      }
    });
    
    // Similar logic for container sizes
    const allContainerSizes = new Set<number>();
    relevantTariffs.forEach(t => {
      if (t.container_size) allContainerSizes.add(t.container_size);
    });
    
    allContainerSizes.forEach(containerSizeId => {
      if (!containerSizes.has(containerSizeId)) {
        const containerSize = relevantTariffs.find(t => t.container_size === containerSizeId)?.container_size_data;
        const originPort = relevantTariffs.find(t => t.origin_port === originId)?.origin_port_data;
        const destPort = relevantTariffs.find(t => t.destination_port === destId)?.destination_port_data;
        const transportType = relevantTariffs.find(t => t.transport_type === transportId)?.transport_type_data;
        
        if (containerSize && originPort && destPort && transportType) {
          suggestions.push({
            originPortId: originId,
            destinationPortId: destId,
            transportTypeId: transportId,
            containerSizeId: containerSizeId,
            originPortName: originPort.name,
            destinationPortName: destPort.name,
            transportTypeName: transportType.name,
            containerSizeName: containerSize.name,
            score: popularity * 1.5,
            reason: `Popular route ${originPort.name} → ${destPort.name} is missing the ${containerSize.name} container size`
          });
        }
      }
    });
    
    // And brackets
    const allBrackets = new Set<number>();
    relevantTariffs.forEach(t => {
      if (t.tariff_bracket) allBrackets.add(t.tariff_bracket);
    });
    
    allBrackets.forEach(bracketId => {
      if (!brackets.has(bracketId)) {
        const bracket = relevantTariffs.find(t => t.tariff_bracket === bracketId)?.tariff_bracket_data;
        const originPort = relevantTariffs.find(t => t.origin_port === originId)?.origin_port_data;
        const destPort = relevantTariffs.find(t => t.destination_port === destId)?.destination_port_data;
        const transportType = relevantTariffs.find(t => t.transport_type === transportId)?.transport_type_data;
        
        if (bracket && originPort && destPort && transportType) {
          suggestions.push({
            originPortId: originId,
            destinationPortId: destId,
            transportTypeId: transportId,
            bracketId: bracketId,
            originPortName: originPort.name,
            destinationPortName: destPort.name,
            transportTypeName: transportType.name,
            bracketName: `${bracket.from}-${bracket.to}`,
            score: popularity,
            reason: `Popular route ${originPort.name} → ${destPort.name} is missing the ${bracket.from}-${bracket.to} bracket`
          });
        }
      }
    });
  });
  
  // Find reverse routes (destinations that are popular origins and vice versa)
  const origins = new Set<number>();
  const destinations = new Set<number>();
  
  relevantTariffs.forEach(t => {
    if (t.origin_port) origins.add(t.origin_port);
    if (t.destination_port) destinations.add(t.destination_port);
  });
  
  // Look for one-way routes (routes that exist in one direction but not the other)
  existingCombinations.forEach(key => {
    const [originId, destId, transportId] = key.split('-').map(Number);
    const reverseKey = `${destId}-${originId}-${transportId}`;
    
    if (!existingCombinations.has(reverseKey) && destinations.has(originId) && origins.has(destId)) {
      const originPort = relevantTariffs.find(t => t.origin_port === destId)?.origin_port_data;
      const destPort = relevantTariffs.find(t => t.destination_port === originId)?.destination_port_data;
      const transportType = relevantTariffs.find(t => t.transport_type === transportId)?.transport_type_data;
      
      if (originPort && destPort && transportType) {
        suggestions.push({
          originPortId: destId,
          destinationPortId: originId,
          transportTypeId: transportId,
          originPortName: originPort.name,
          destinationPortName: destPort.name,
          transportTypeName: transportType.name,
          score: 8, // High score for reverse routes
          reason: `Consider adding the reverse route ${originPort.name} → ${destPort.name}`
        });
      }
    }
  });
  
  // Sort suggestions by score (highest first)
  return suggestions.sort((a, b) => b.score - a.score);
};

// Get tariff suggestions for a specific route
export const getTariffSuggestionsForRoute = (
  tariffs: Tariff[],
  originPortId: number,
  destinationPortId: number,
  transportTypeId: number
): RouteCombination[] => {
  const allSuggestions = getMissingRouteCombinations(tariffs);
  
  return allSuggestions.filter(
    suggestion => 
      suggestion.originPortId === originPortId &&
      suggestion.destinationPortId === destinationPortId &&
      suggestion.transportTypeId === transportTypeId
  );
};

// Interface for overlapping tariff information
export interface OverlappingTariff {
  tariff1: Tariff;
  tariff2: Tariff;
  overlapStart: string;
  overlapEnd: string;
  overlapDays: number;
  severity: 'low' | 'medium' | 'high';
}

/**
 * Find tariffs with overlapping date ranges that share the same key attributes
 * @param tariffs List of tariffs to check
 * @param activeOnly Only check active, future, and expiring tariffs
 * @returns Array of overlapping tariff pairs with details about overlap
 */
export const getOverlappingTariffs = (
  tariffs: Tariff[],
  activeOnly: boolean = true
): OverlappingTariff[] => {
  // If activeOnly is true, only consider active, future, and expiring tariffs
  const relevantTariffs = activeOnly
    ? tariffs.filter(tariff => {
        const status = getTariffStatus(tariff);
        return status === "active" || status === "future" || status === "expiring";
      })
    : tariffs;
  
  // Filter out incomplete tariffs (those missing dates)
  const completeTariffs = relevantTariffs.filter(
    tariff => tariff.from !== null && tariff.to !== null
  );
  
  const overlaps: OverlappingTariff[] = [];
  
  // Generate a key for each tariff based on important attributes
  const getTariffKey = (tariff: Tariff): string => {
    return [
      tariff.transport_type,
      tariff.origin_port,
      tariff.destination_port,
      tariff.container_size,
      tariff.bookings_type,
      tariff.tariff_bracket
    ].join('-');
  };
  
  // Check each pair of tariffs
  for (let i = 0; i < completeTariffs.length; i++) {
    const tariff1 = completeTariffs[i];
    const tariff1Key = getTariffKey(tariff1);
    
    for (let j = i + 1; j < completeTariffs.length; j++) {
      const tariff2 = completeTariffs[j];
      const tariff2Key = getTariffKey(tariff2);
      
      // Only compare tariffs with the same key attributes
      if (tariff1Key === tariff2Key) {
        const start1 = dayjs(tariff1.from);
        const end1 = dayjs(tariff1.to);
        const start2 = dayjs(tariff2.from);
        const end2 = dayjs(tariff2.to);
        
        // Check for overlap
        // Two date ranges overlap if the start of each range is before the end of the other
        if (start1.isBefore(end2) && start2.isBefore(end1)) {
          // Calculate the overlap period
          const overlapStart = start1.isAfter(start2) ? start1 : start2;
          const overlapEnd = end1.isBefore(end2) ? end1 : end2;
          const overlapDays = overlapEnd.diff(overlapStart, 'day');
          
          // Determine severity based on overlap and tariff prices
          let severity: 'low' | 'medium' | 'high' = 'medium';
          
          // If prices differ significantly or overlap is large, it's high severity
          if (tariff1.price && tariff2.price) {
            const priceDiff = Math.abs(tariff1.price - tariff2.price);
            const pricePercent = priceDiff / Math.max(tariff1.price, tariff2.price);
            
            if (pricePercent > 0.1 || overlapDays > 30) {
              severity = 'high';
            } else if (pricePercent < 0.05 && overlapDays < 7) {
              severity = 'low';
            }
          } else {
            // If any price is missing, consider it high severity
            severity = 'high';
          }
          
          overlaps.push({
            tariff1,
            tariff2,
            overlapStart: overlapStart.format('YYYY-MM-DD'),
            overlapEnd: overlapEnd.format('YYYY-MM-DD'),
            overlapDays,
            severity
          });
        }
      }
    }
  }
  
  // Sort by severity (high to low) and then by overlap days (high to low)
  return overlaps.sort((a, b) => {
    if (a.severity !== b.severity) {
      return a.severity === 'high' ? -1 : a.severity === 'medium' ? (b.severity === 'high' ? 1 : -1) : 1;
    }
    return b.overlapDays - a.overlapDays;
  });
};